import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import jwt_decode from "jwt-decode";
import algoliasearch from 'algoliasearch/lite';


import { getInstance } from '@/auth'
import FleetStore from '@/store/api_stores/fleets'
import PresetsStore from '@/store/api_stores/presets'
import TradeflowsStore from '@/store/api_stores/tradeflows'
import LivestatesStore from '@/store/api_stores/livestates'
import CongestionStore from '@/store/api_stores/congestion'
import TonnageStore from '@/store/api_stores/tonnage'
import VesselsStore from '@/store/api_stores/vessels'
import DrydockStore from '@/store/api_stores/drydock'
import FeedbackStore from '@/store/api_stores/feedback'
import ApikeysStore from '@/store/api_stores/apikeys'
import isEmpty from 'lodash-es/isEmpty';


axios.defaults.paramsSerializer = function(params) {
  let resultItems = Object.keys(params).map(
    key => `${key}=${encodeURIComponent(params[key])}`
  );
  return resultItems.join('&');
};


const DEBUG = false;  // console.log(...) for request/response
const log = (...args) => {
  if(DEBUG) {
    console.log(...args);
  }
};

function expiredToken(token) {
  const decoded = jwt_decode(token);
  const expired = decoded.exp * 1000 < Date.now() - 10000;
  return expired;
}


Vue.use(Vuex);

export default {
  namespaced: true,
  modules: {
    tradeflows: TradeflowsStore,
    livestates: LivestatesStore,
    congestion: CongestionStore,
    tonnage: TonnageStore,
    drydock: DrydockStore,
    fleets: FleetStore,
    vessels: VesselsStore,

    presets: PresetsStore,
    apikeys: ApikeysStore,
    feedback: FeedbackStore,
  },

  state: () => {
    return {
      authClient: null,
      apiToken: null,
      user: null,
      searchClient: null,
      activeRequestsCount: 0,
    };
  },

  getters: {
    apiRequestHeaders(state) {
      // sets auth header if apiToken is available
      let headers = {
        // cache-controls is denied in cors currently
        // 'Cache-Control': 'no-cache',
      };
      if (state.apiToken) {
        headers['Authorization'] = `Bearer ${state.apiToken}`;
      }
      return headers;
    },
    getActiveRequestsCount(state) {
      return state.activeRequestsCount;
    }
  },


  mutations: {
    setFiltersSchema(state, value) {
      state.filtersSchema = value;
    },
    setAuthClient(state, value) {
      state.authClient = value;
    },
    setAPIToken(state, value) {
      state.apiToken = value;
    },
    setUser(state, value) {
      state.user = value;
    },
    setActiveRequestsCount(state, value) {
      state.activeRequestsCount = value;
    },
    setSearchClient(state, value) {
      state.searchClient = value;
    },
  },

  actions: {
    // *** algolia search client *** 
    async getSearchClient({commit, state}) {
      if(state.searchClient) {
        return state.searchClient;
      }

      const searchService = algoliasearch(
        process.env.VUE_APP_ALGOLIA_APP_ID,
        process.env.VUE_APP_ALGOLIA_SEARCH_API_KEY,
      )
      commit('setSearchClient', searchService);

      return searchService;
    },

    async algoliaSearch(store, parameters) {
      // "parameters.searchIndex" is a search index
      // "parameters.query" is a query string for search
      // "parameters.searchParameters" are algolia parameters, like:
      // {
      //   "hitsPerPage": 10,
      //   "responseFields": "*",
      //   "page": 0,
      //   "facets": ["*"],
      // }

      const searchClient = await store.dispatch('getSearchClient');
      const searchIndex = searchClient.initIndex(parameters.searchIndex);

      if(isEmpty(parameters.searchParameters)) {
        return searchIndex.search(parameters.query);
      } else {
        return searchIndex.search(parameters.query, parameters.searchParameters);
      }
    },

    // *** authentication requests ***
    async getAuthClient({commit, state}) {
      if(state.authClient) {
        return state.authClient;
      }

      const authService = getInstance();

      if (authService.loading) {
        // it seems it's an impossible case here, it's left just in case to ensure
        // this case it's impossible to return auth0Client from this action
        console.error('auth service is not loaded yet');

        authService.$watch('loading', () => {
          commit('setAuthClient', authService.auth0Client);
        })
      } else {
        commit('setAuthClient', authService.auth0Client);
      }

      return authService.auth0Client;
    },

    async is_admin({state, commit, dispatch}) {
      const user = await dispatch('getUser');
      const admin = user['https://app.oceanbolt.com/user_authorization'].permissions.includes("admin") || false
      return admin
    },

    async getToken({state, commit, dispatch}) {
      if(state.apiToken && !expiredToken(state.apiToken)) {
        return state.apiToken;
      }
      const authClient = await dispatch('getAuthClient');
      const apiToken = await authClient.getTokenSilently();
      commit('setAPIToken', apiToken);

      //console.log('TOKEN IS RETRIEVED SILENTLY');
      return apiToken;
    },

    async getUser({commit, state, dispatch}) {
      if(state.user) {
        return state.user;
      }

      const authClient = await dispatch('getAuthClient');
      const user = await authClient.getUser();  // todo: handle null
      commit('setUser', user);
      return user;
    },

    async logout({dispatch}) {
      const authClient = await dispatch('getAuthClient');
      await authClient.logout({returnTo: window.location.origin});
    },

    // * low level api section *
    async apiRequest({getters, dispatch, commit}, {baseUrl, method, apiUrl, data, headers}) {
      log('apiRequest', method, apiUrl, JSON.stringify(data, null, 2));

      // common method to get/send data to the API
      const dataMethods = ['post', 'put'];  // data will be sent in a request body
      const paramsMethods = ['get'];  // data will be sent as get parameters

      // available as state.apiToken in common situations,
      // otherwise it will be retrieved silently
      let token = await dispatch('getToken');
      if(!token) {
        console.error('unable to retrieve a token!');
        return;
      }


      let requestData = {
        headers: {...getters.apiRequestHeaders, ...headers || {}},
        method: method,
        url: `${baseUrl || process.env.VUE_APP_API_URL}${apiUrl}`,
        data:  dataMethods.includes(method) ? data : null,
        params: paramsMethods.includes(method) ? data : null,
      };

      try {
        commit('setActiveRequestsCount', getters.getActiveRequestsCount + 1);
        let result = await axios(requestData);
        commit('setActiveRequestsCount', getters.getActiveRequestsCount - 1);

        log(`api result ${apiUrl}`, result);
        return {
          error: false,
          status_code: result.status,
          data: result.data,
          requestData: requestData,
        };
      } catch (error) {
        commit('setActiveRequestsCount', getters.getActiveRequestsCount - 1);
        log(`api error ${apiUrl}`, error);
        if(error.response) {
          // http errors: 404, 403, 500, etc
          return {
            error: true,
            status_code: error.response.status,
            error_details: error.response.data,
            requestData: requestData,
          };
        } else {
          // network error for example
          return {
            error: true,
            error_details: [error.message],
            requestData: requestData,
          }
        }
      }
    },
  }
}
